import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import apiEndpointManager from "../../config/index";
import { RootState } from "../store";

interface JwtPayload {
    sub: string; // Cognito uses 'sub' for userId
    email: string;
    token_use: string;
    exp: number;
}

export interface Location {
    country: string;
    state: string;
    city: string;
}

export interface Experience {
    companyName: string;
    jobTitle: string;
    employmentType: string;
    country: string;
    locationType: string;
    currentlyWorkHere: boolean;
    startDate: string;
    endDate: string;
    description: string;
}

export interface Education {
    school: string;
    degree: string;
    fieldOfStudy: string;
    grade?: string;
    from: string;
    to?: string;
    current: boolean;
    description?: string;
}

export interface VolunteerExperience {
    organization: string;
    role: string;
    cause: string;
    currentlyVolunteerHere: boolean;
    startDate: string;
    endDate: string;
    description: string;
}

export interface LicenseCertification {
    name: string;
    issuingOrganization: string;
    issueDate: string;
    expirationDate?: string;
    credentialID?: string;
    credentialURL?: string;
    neverExpires: boolean;
}

// This should be the profile data that is stored in dynamodb
interface Profile {
    id: string; // profileId
    userId: string;
    location?: Location;
    photoUrl?: string;
    backgroundPhotoUrl?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    title?: string;
    linkedinURL?: string;
    experience?: Experience[];
    education?: Education[];
    volunteerExperience?: VolunteerExperience[];
    licenseCertification?: LicenseCertification[];
    about?: string;
    createdAt?: string;
    updatedAt?: string;
    initialSetupComplete: boolean | null;
    professionalStatus?: string;
    joiningReason?: string;
    isUserApproved?: boolean;
}

// NEW: Interface for the state of fetching a single external profile
export interface IndividualProfileFetchState {
    data: Profile;
    // Revert loading type to string literal union
    loading: "idle" | "pending" | "succeeded" | "failed";
    error: string | null;
    // Add connection status fields here as well
    connectionRequestStatus: "idle" | "loading" | "succeeded" | "failed";
    connectionRequestError: string | null;
}

export interface ProfileState extends Omit<Profile, "initialSetupComplete"> {
    // This top-level state represents the LOGGED-IN USER'S profile data.
    // It is fetched by `fetchFullProfile` and should only be modified for the authenticated user.
    loading: boolean; // Loading state for the main user's profile (fetchFullProfile)
    error: string | null; // Error state for the main user's profile
    connectionRequestStatus: "idle" | "loading" | "succeeded" | "failed";
    connectionRequestError: string | null;
    initialSetupComplete: boolean | null; // Allow null during initial load

    // This map stores the profiles of OTHER users that have been viewed.
    // It is populated by the `fetchProfile` thunk, with the user's ID as the key.
    // This prevents the logged-in user's state from being polluted when viewing other profiles.
    profiles?: Record<string, IndividualProfileFetchState>;
}

// UPDATED: Initialize the 'profiles' map
const initialState: ProfileState = {
    location: { country: "", state: "", city: "" },
    firstName: "",
    lastName: "",
    title: "",
    photoUrl: "",
    backgroundPhotoUrl: "",
    experience: [],
    education: [],
    volunteerExperience: [],
    licenseCertification: [],
    about: "", // Renamed from status
    linkedinURL: "",
    loading: false,
    error: null,
    id: "",
    userId: "",
    createdAt: "",
    updatedAt: "",
    connectionRequestStatus: "idle",
    connectionRequestError: null,
    // Initialize the new profiles map as an empty object
    profiles: {},
    initialSetupComplete: false,
    professionalStatus: "",
    isUserApproved: false,
    joiningReason: "", // Initialize as empty string
};

// this fetches the profile data along with the profile photo, no need to make a separate call to fetch the profile photo
export const fetchFullProfile = createAsyncThunk(
    "profile/fetchFullProfile",
    async (token: string, { rejectWithValue }) => {
        try {
            const decodedToken = jwtDecode<JwtPayload>(token);
            const userId = decodedToken.sub;
            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await axios.get(`${endpoint}/profiles?userId=${userId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.data.length === 0) {
                return rejectWithValue("No profile data found");
            }
            const profileData: Profile = response.data.profile;
            return profileData;
        } catch (error: any) {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            return rejectWithValue(
                err.response?.data?.message || err.message || "Failed to fetch profile",
            );
        }
    },
);

// this calls the same endpoint as fetchFullProfile but it doesnt update the global profile state, only returns the profile data, doesnt have to be here in the slice, since it has nothing to do with the profile statew
// This thunk fetches a specific user's profile by their ID.
// The fetched data is stored in the `profiles` map in the state, NOT at the top level.
export const fetchProfile = createAsyncThunk(
    "profile/fetchProfile",
    async (userId: string, { rejectWithValue }) => {
        try {
            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await axios.get(`${endpoint}/profiles?userId=${userId}`);
            if (response.data.length === 0) {
                return rejectWithValue("No profile data found");
            }
            // console.log("profile data", response.data.profile);
            const profileData: Profile = response.data.profile;
            return profileData;
        } catch (error: any) {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            return rejectWithValue(
                err.response?.data?.message || err.message || "Failed to fetch profile",
            );
        }
    },
);

// This thunk should ONLY be used to fetch the logged-in user's profile photo.
// It updates the top-level `photoUrl` in the state.
export const fetchProfilePhoto = createAsyncThunk(
    "profile/fetchProfilePhoto",
    async (userId: string, { rejectWithValue }) => {
        try {
            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await axios.get(
                `${endpoint}/profileImages/getProfileImage?userId=${encodeURIComponent(userId)}&imageType=profilePhoto`,
            );
            const data = response.data;
            if (response.status === 200) {
                return { imageUrl: data.publicUrl };
            } else {
                return rejectWithValue(data.message || "Failed to fetch profile photo");
            }
        } catch (error: unknown) {
            const err = error as Error;
            return rejectWithValue(err.message || "Failed to fetch profile photo");
        }
    },
);

// New async thunk to upload a profile photo
// This thunk should ONLY be used to upload the logged-in user's profile photo.
// It updates the top-level `photoUrl` in the state upon success.
export const uploadProfilePhoto = createAsyncThunk(
    "profile/uploadProfilePhoto",
    async ({ token, photoData }: { token: string; photoData: string }, { rejectWithValue }) => {
        try {
            const decodedToken = jwtDecode<JwtPayload>(token);
            const userId = decodedToken.sub;
            const payload = {
                image: photoData,
                userId: userId,
                imageType: "profilePhoto",
            };
            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await axios.post(`${endpoint}/profileImages/uploadImage`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = response.data;
            if (response.status === 200) {
                return {
                    success: true,
                    message: "Profile photo uploaded successfully",
                    photoUrl: data.imageUrl,
                };
            } else {
                return rejectWithValue(data.message || "Failed to upload profile photo");
            }
        } catch (error: unknown) {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            return rejectWithValue(
                err.response?.data?.message || err.message || "Failed to upload profile photo",
            );
        }
    },
);

// New async thunk to post the profile
// ! DEPRECATED
export const postProfile = createAsyncThunk(
    "profile/postProfile",
    async (
        { token, profileData }: { token: string; profileData: ProfileState },
        { rejectWithValue },
    ) => {
        const decodedToken = jwtDecode<JwtPayload>(token);
        const userId = decodedToken.sub;

        try {
            const payload: Partial<Profile> = {
                userId,
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                title: profileData.title,
                location: profileData.location,
                about: profileData.about,
                experience: profileData.experience,
                education: profileData.education,
                photoUrl: profileData.photoUrl,
                volunteerExperience: profileData.volunteerExperience,
                licenseCertification: profileData.licenseCertification,
                linkedinURL: profileData.linkedinURL,
            };

            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await axios.post(`${endpoint}/profiles`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error: unknown) {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            return rejectWithValue(
                err.response?.data?.message || err.message || "Failed to post profile",
            );
        }
    },
);

export const updateProfile = createAsyncThunk(
    "profile/updateProfile",
    async (
        { token, profileData }: { token: string; profileData: ProfileState },
        { rejectWithValue },
    ) => {
        // Decode the token to reliably get the cognito user ID (sub)
        const decodedToken = jwtDecode<JwtPayload>(token);
        // This userId is the authenticated user performing the update.
        const userId = decodedToken.sub;

        try {
            // Construct the payload for the backend PUT request.
            // It includes the profileId (profileData.id) which is essential for the backend
            // to identify *which* profile record to update.
            // It also includes the userId from the token to ensure the backend can
            // potentially validate ownership or log the updater, although the primary
            // identification for the PUT operation itself is usually the profileId (payload.id).
            const payload: Partial<Profile> = {
                id: profileData.id, // The unique ID of the profile record in the database.
                userId: userId, // The Cognito User ID of the user making the change.
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                title: profileData.title,
                location: profileData.location,
                about: profileData.about,
                experience: profileData.experience,
                education: profileData.education,
                photoUrl: profileData.photoUrl,
                volunteerExperience: profileData.volunteerExperience,
                licenseCertification: profileData.licenseCertification,
                linkedinURL: profileData.linkedinURL,
                professionalStatus: profileData.professionalStatus,
                joiningReason: profileData.joiningReason,
                initialSetupComplete: profileData.initialSetupComplete,
            };

            // Get the API endpoint from the configuration.
            const endpoint = await apiEndpointManager.getEndpoint();

            // Send the payload to the backend using a PUT request.
            // The backend should use the `id` in the payload to find and update the correct profile.
            const response = await axios.put(`${endpoint}/profiles`, payload, {
                headers: {
                    // Pass the JWT token for authentication/authorization.
                    Authorization: `Bearer ${token}`,
                },
            });

            // Return the data received from the backend (usually the updated profile).
            return response.data;
        } catch (error: unknown) {
            // Handle potential errors during the API call.
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            console.error("Profile update error:", err?.response?.data || err?.message || err);
            return rejectWithValue(err.response?.data?.message || "Failed to update profile");
        }
    },
);

// New async thunk to delete a profile photo
export const deleteProfilePhoto = createAsyncThunk(
    "profile/deleteProfilePhoto",
    async (userId: string, { rejectWithValue }) => {
        try {
            const endpoint = await apiEndpointManager.getEndpoint();
            const url = `${endpoint}/profileImages/deleteProfileImage?userId=${encodeURIComponent(userId)}`;
            const response = await fetch(url, {
                method: "POST",
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "Failed to delete profile photo");
            }

            return {
                success: true,
                message: "Profile photo deleted successfully",
            };
        } catch (error: unknown) {
            const err = error as Error;
            return rejectWithValue(err.message || "Failed to delete profile photo");
        }
    },
);

// NEW: Async thunk to send a connection request
export const sendConnectionRequest = createAsyncThunk(
    "profile/sendConnectionRequest",
    async (
        {
            requesterId,
            recipientId,
            token,
        }: { requesterId: string; recipientId: string; token: string },
        { rejectWithValue },
    ) => {
        try {
            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await axios.post(
                `${endpoint}/connections`, // Use the new endpoint
                { requesterId, recipientId }, // Send IDs in the body
                {
                    headers: {
                        Authorization: `Bearer ${token}`, // Add auth token
                    },
                },
            );
            // Assuming backend returns connectionId and status on success (201)
            return response.data;
        } catch (error: unknown) {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            console.error("Send connection request error:", err.response?.data || err.message);
            return rejectWithValue(
                err.response?.data?.message || "Failed to send connection request",
            );
        }
    },
);

// Define the shape of the data needed for the initial setup update
export interface InitialProfileUpdateData {
    location?: Location;
    title?: string;
    about?: string;
    professionalStatus?: string;
    joiningReason?: string;
    initialSetupComplete?: boolean;
}

// Define the argument type for the thunk
interface UpdateInitialProfileArgs {
    token: string;
    profileId: string;
    data: InitialProfileUpdateData;
}

// Define the payload type for the fulfilled action of updateInitialProfile
interface UpdateInitialProfileFulfilledPayload {
    title?: string;
    about?: string;
    professionalStatus?: string;
    location?: Location;
    initialSetupComplete: boolean;
    joiningReason?: string;
    isUserApproved?: boolean;
}

// *** NEW: Async thunk to update initial profile setup ***
export const updateInitialProfile = createAsyncThunk<
    UpdateInitialProfileFulfilledPayload,
    UpdateInitialProfileArgs
>("profile/updateInitialProfile", async ({ token, profileId, data }, { rejectWithValue }) => {
    try {
        const endpoint = await apiEndpointManager.getEndpoint();
        const response = await axios.patch(`${endpoint}/profiles/${profileId}`, data, {
            headers: { Authorization: `Bearer ${token}` },
        });
        // Ensure the returned payload matches the UpdateInitialProfileFulfilledPayload interface
        return response.data as UpdateInitialProfileFulfilledPayload;
    } catch (error: any) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        return rejectWithValue(err.response?.data?.message || err.message || "Update failed");
    }
});

// This thunk should ONLY be used to upload the logged-in user's background photo.
// It updates the top-level `backgroundPhotoUrl` in the state upon success.
export const uploadProfileBackgroundPhoto = createAsyncThunk(
    "profile/uploadProfileBackgroundPhoto",
    async ({ token, photoData }: { token: string; photoData: string }, { rejectWithValue }) => {
        try {
            const decodedToken = jwtDecode<JwtPayload>(token);
            const userId = decodedToken.sub;
            const payload = {
                image: photoData,
                userId: userId,
                imageType: "profileBackgroundPhoto",
            };
            const endpoint = await apiEndpointManager.getEndpoint();
            const response = await axios.post(`${endpoint}/profileImages/uploadImage`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = response.data;
            if (response.status === 200) {
                return {
                    success: true,
                    message: "Profile background photo uploaded successfully",
                    backgroundPhotoUrl: data.imageUrl,
                };
            } else {
                return rejectWithValue(data.message || "Failed to upload profile background photo");
            }
        } catch (error: unknown) {
            const err = error as { response?: { data?: { message?: string } }; message?: string };
            return rejectWithValue(
                err.response?.data?.message || "Failed to upload profile background photo",
            );
        }
    },
);

// *** NEW: Async thunk to fetch a profile background photo ***
// This thunk should ONLY be used to fetch the logged-in user's background photo.
// It updates the top-level `backgroundPhotoUrl` in the state.
export const fetchProfileBackgroundPhoto = createAsyncThunk(
    "profile/fetchProfileBackgroundPhoto",
    async (userId: string, { rejectWithValue }) => {
        try {
            const endpoint = await apiEndpointManager.getEndpoint();
            // Assuming endpoint can differentiate via query param
            // console.log("Fetching profile background photo for user:", userId);
            const response = await axios.get(
                `${endpoint}/profileImages/getProfileImage?userId=${encodeURIComponent(userId)}&imageType=profileBackgroundPhoto`,
            );
            const data = response.data;
            if (response.status === 200) {
                return { imageUrl: data.publicUrl };
            } else {
                return rejectWithValue(data.message || "Failed to fetch profile background photo");
            }
        } catch (error: unknown) {
            const err = error as Error;
            return rejectWithValue(err.message || "Failed to fetch profile background photo");
        }
    },
);

const profileSlice = createSlice({
    name: "profile",
    initialState,
    reducers: {
        setLocation(state, action: PayloadAction<Location>) {
            // Check if location exists before assigning
            if (!state.location) {
                state.location = { country: "", state: "", city: "" };
            }
            state.location.country = action.payload.country;
            state.location.state = action.payload.state;
            state.location.city = action.payload.city;
        },
        setProfilePhoto(state, action: PayloadAction<string>) {
            state.photoUrl = action.payload;
        },
        addExperience(state, action: PayloadAction<Experience>) {
            if (state.experience) {
                state.experience.push(action.payload);
            } else {
                state.experience = [action.payload];
            }
        },
        updateExperience(state, action: PayloadAction<{ index: number; experience: Experience }>) {
            const { index, experience } = action.payload;
            if (state.experience) {
                state.experience[index] = experience;
            }
        },
        setEducation(state, action: PayloadAction<Education>) {
            state.education = [...(state.education || []), action.payload];
        },
        updateEducation(state, action: PayloadAction<{ index: number; education: Education }>) {
            const { index, education } = action.payload;
            if (state.education) {
                state.education[index] = education;
            }
        },
        addVolunteerExperience(state, action: PayloadAction<VolunteerExperience>) {
            if (state.volunteerExperience) {
                state.volunteerExperience.push(action.payload);
            } else {
                state.volunteerExperience = [action.payload];
            }
        },
        updateVolunteerExperience(
            state,
            action: PayloadAction<{ index: number; volunteerExperience: VolunteerExperience }>,
        ) {
            const { index, volunteerExperience } = action.payload;
            if (state.volunteerExperience) {
                state.volunteerExperience[index] = volunteerExperience;
            }
        },
        addLicenseCertification(state, action: PayloadAction<LicenseCertification>) {
            if (state.licenseCertification) {
                state.licenseCertification.push(action.payload);
            } else {
                state.licenseCertification = [action.payload];
            }
        },
        updateLicenseCertification(
            state,
            action: PayloadAction<{ index: number; licenseCertification: LicenseCertification }>,
        ) {
            const { index, licenseCertification } = action.payload;
            if (state.licenseCertification) {
                state.licenseCertification[index] = licenseCertification;
            }
        },
        setStatus(state, action: PayloadAction<string>) {
            state.about = action.payload;
        },
        setAbout(state, action: PayloadAction<string>) {
            state.about = action.payload;
        },
        setFirstName(state, action: PayloadAction<string>) {
            state.firstName = action.payload;
        },
        setLastName(state, action: PayloadAction<string>) {
            state.lastName = action.payload;
        },
        setTitle(state, action: PayloadAction<string>) {
            state.title = action.payload;
        },
        setLinkedinURL(state, action: PayloadAction<string>) {
            state.linkedinURL = action.payload;
        },
        resetConnectionStatus(state) {
            state.connectionRequestStatus = "idle";
            state.connectionRequestError = null;
        },
        setProfessionalStatus(state, action: PayloadAction<string>) {
            state.professionalStatus = action.payload;
        },
        markInitialSetupComplete(state) {
            state.initialSetupComplete = true;
        },
        deleteExperience(state, action: PayloadAction<number>) {
            if (state.experience) {
                state.experience.splice(action.payload, 1);
            }
        },
        deleteEducation(state, action: PayloadAction<number>) {
            if (state.education) {
                state.education.splice(action.payload, 1);
            }
        },
        deleteVolunteerExperience(state, action: PayloadAction<number>) {
            if (state.volunteerExperience) {
                state.volunteerExperience.splice(action.payload, 1);
            }
        },
        deleteLicenseCertification(state, action: PayloadAction<number>) {
            if (state.licenseCertification) {
                state.licenseCertification.splice(action.payload, 1);
            }
        },
    },
    extraReducers: (builder) => {
        // Start a SINGLE chain for all cases
        builder
            // Handle fetchFullProfile
            .addCase(fetchFullProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchFullProfile.fulfilled, (state, action: PayloadAction<Profile>) => {
                Object.assign(state, action.payload);
                state.loading = false;
                state.error = null;
                state.initialSetupComplete = action.payload.initialSetupComplete ?? false;
                state.professionalStatus = action.payload.professionalStatus ?? "";
            })
            .addCase(fetchFullProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Handle postProfile
            .addCase(postProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(postProfile.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    Object.keys(action.payload).forEach((key) => {
                        if (key in state) {
                            // @ts-expect-error - dynamic access
                            state[key as keyof ProfileState] =
                                action.payload[key as keyof typeof action.payload];
                        }
                    });
                }
            })
            .addCase(postProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Handle updateProfile
            .addCase(updateProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateProfile.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    Object.keys(action.payload).forEach((key) => {
                        if (key in state) {
                            // @ts-expect-error - dynamic access
                            state[key as keyof ProfileState] =
                                action.payload[key as keyof typeof action.payload];
                        }
                    });
                }
            })
            .addCase(updateProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = null;
            })

            // Handle fetchProfilePhoto
            .addCase(fetchProfilePhoto.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProfilePhoto.fulfilled, (state, action) => {
                state.loading = false;
                state.photoUrl = action.payload.imageUrl;
            })
            .addCase(fetchProfilePhoto.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Handle uploadProfilePhoto
            .addCase(uploadProfilePhoto.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                uploadProfilePhoto.fulfilled,
                (state, action: PayloadAction<{ success: boolean; photoUrl: string }>) => {
                    state.loading = false;
                    if (action.payload.success && action.payload.photoUrl) {
                        state.photoUrl = action.payload.photoUrl;
                    }
                },
            )
            .addCase(uploadProfilePhoto.rejected, (state, action) => {
                state.loading = false;
                state.error = null;
            })

            // Handle uploadProfileBackgroundPhoto
            .addCase(uploadProfileBackgroundPhoto.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                uploadProfileBackgroundPhoto.fulfilled,
                (
                    state,
                    action: PayloadAction<{ success: boolean; backgroundPhotoUrl: string }>,
                ) => {
                    state.loading = false;
                    if (action.payload.success && action.payload.backgroundPhotoUrl) {
                        state.backgroundPhotoUrl = action.payload.backgroundPhotoUrl;
                    }
                },
            )
            .addCase(uploadProfileBackgroundPhoto.rejected, (state, action) => {
                state.loading = false;
                state.error = null;
            })

            // Handle fetchProfileBackgroundPhoto
            .addCase(fetchProfileBackgroundPhoto.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProfileBackgroundPhoto.fulfilled, (state, action) => {
                state.loading = false;
                state.backgroundPhotoUrl = action.payload.imageUrl;
            })
            .addCase(fetchProfileBackgroundPhoto.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Handle deleteProfilePhoto
            .addCase(deleteProfilePhoto.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteProfilePhoto.fulfilled, (state) => {
                state.loading = false;
                state.photoUrl = "";
            })
            .addCase(deleteProfilePhoto.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Handle sendConnectionRequest
            .addCase(sendConnectionRequest.pending, (state) => {
                state.connectionRequestStatus = "loading";
                state.connectionRequestError = null;
            })
            .addCase(sendConnectionRequest.fulfilled, (state, action) => {
                state.connectionRequestStatus = "succeeded";
                // console.log("Connection request successful:", action.payload);
            })
            .addCase(sendConnectionRequest.rejected, (state, action) => {
                state.connectionRequestStatus = "failed";
                state.connectionRequestError = action.payload as string;
            })

            // Handle fetchProfile
            .addCase(fetchProfile.pending, (state, action) => {
                const userId = action.meta.arg;
                if (!state.profiles) state.profiles = {};
                state.profiles[userId] = {
                    ...(state.profiles[userId] || {}),
                    loading: "pending",
                    error: null,
                };
            })
            .addCase(fetchProfile.fulfilled, (state, action) => {
                const profileData = action.payload;
                const userId = profileData.userId;
                if (!state.profiles) state.profiles = {};
                state.profiles[userId] = {
                    data: profileData,
                    loading: "succeeded",
                    error: null,
                    connectionRequestStatus: "idle",
                    connectionRequestError: null,
                };
            })
            .addCase(fetchProfile.rejected, (state, action) => {
                const userId = action.meta.arg;
                if (!state.profiles) state.profiles = {};
                state.profiles[userId] = {
                    ...(state.profiles[userId] || {}),
                    loading: "failed",
                    error: (action.payload as string) ?? "Unknown error",
                };
            })

            // Cases for updateInitialProfile
            .addCase(updateInitialProfile.pending, (state: ProfileState) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                updateInitialProfile.fulfilled,
                (
                    state: ProfileState,
                    action: PayloadAction<UpdateInitialProfileFulfilledPayload>,
                ) => {
                    state.loading = false;
                    if (action.payload) {
                        state.title = action.payload.title ?? state.title;
                        state.about = action.payload.about ?? state.about;
                        state.professionalStatus =
                            action.payload.professionalStatus ?? state.professionalStatus;
                        if (action.payload.location) {
                            state.location = { ...state.location, ...action.payload.location };
                        }
                        state.initialSetupComplete = action.payload.initialSetupComplete;
                        state.joiningReason = action.payload.joiningReason ?? state.joiningReason;
                    }
                },
            )
            .addCase(updateInitialProfile.rejected, (state: ProfileState, action) => {
                state.loading = false;
                state.error = action.payload as string;
            }); // End the single chain here
    },
});

export const {
    setLocation,
    setProfilePhoto,
    addExperience,
    updateExperience,
    setEducation,
    updateEducation,
    addVolunteerExperience,
    updateVolunteerExperience,
    addLicenseCertification,
    updateLicenseCertification,
    setStatus,
    setAbout,
    setFirstName,
    setLastName,
    setTitle,
    setLinkedinURL,
    resetConnectionStatus,
    setProfessionalStatus,
    markInitialSetupComplete,
    deleteExperience,
    deleteEducation,
    deleteVolunteerExperience,
    deleteLicenseCertification,
} = profileSlice.actions;

export default profileSlice.reducer;

export const selectProfile = (state: RootState) => state.profile;
export const selectProfilePhoto = (state: RootState) => state.profile.photoUrl;
export const selectUserLocation = (state: RootState) => state.profile.location;

// NEW: Selector for background photo
export const selectProfileBackgroundPhoto = (state: RootState) => state.profile.backgroundPhotoUrl;

// Selects the profile data for a specific user ID from the profiles map
export const selectUserProfileById = (
    state: RootState,
    userId: string,
): Profile | null | undefined => {
    // Check if profiles map and specific user profile exist
    return state.profile.profiles?.[userId]?.data;
};
export const selectConnectionRequestStatus = (state: RootState) =>
    state.profile.connectionRequestStatus;
export const selectConnectionRequestError = (state: RootState) =>
    state.profile.connectionRequestError;

// Add selector for initialSetupComplete if needed
export const selectInitialSetupComplete = (state: RootState) => state.profile.initialSetupComplete;
export const selectFullProfile = (state: RootState): ProfileState => state.profile; // Return full state
export const selectProfileLoading = (state: RootState) => state.profile.loading;

// Add selector for professionalStatus if needed
export const selectProfessionalStatus = (state: RootState) => state.profile.professionalStatus;
